"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, ChevronUp, ChevronDown } from "lucide-react";
import type { NavigationItem } from "../types";
import { splitChildrenByVisibility } from "@/lib/hooks/settings/nav-customization/selectors";

interface SubmenuEditorProps {
  parentId: string;
  items: NavigationItem[];
  onMoveChildUp: (parentId: string, childId: string) => void;
  onMoveChildDown: (parentId: string, childId: string) => void;
  onToggleChildVisibility: (parentId: string, childId: string) => void;
}

interface ChildRowProps {
  child: NavigationItem;
  parentId: string;
  /** When true, render a single "show" action (item lives in the More group). */
  hidden: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMoveChildUp: (parentId: string, childId: string) => void;
  onMoveChildDown: (parentId: string, childId: string) => void;
  onToggleChildVisibility: (parentId: string, childId: string) => void;
}

function ChildRow({
  child,
  parentId,
  hidden,
  isFirst,
  isLast,
  onMoveChildUp,
  onMoveChildDown,
  onToggleChildVisibility,
}: ChildRowProps) {
  const Icon = child.icon;
  return (
    <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
      <div className="flex items-center gap-2 text-sm">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span>{child.name}</span>
      </div>
      <div className="flex items-center gap-1">
        {!hidden && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveChildUp(parentId, child.id)}
              disabled={isFirst}
              className="h-7 w-7 p-0"
              title="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveChildDown(parentId, child.id)}
              disabled={isLast}
              className="h-7 w-7 p-0"
              title="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onToggleChildVisibility(parentId, child.id)}
          className="h-7 w-7 p-0"
          title={hidden ? "Show up front" : "Move to More"}
        >
          {hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

/**
 * Reorder + show/hide editor for one nav item's submenu. Visible children can be
 * reordered (chevrons) or sent to the "More" group (eye-off); More children can
 * be restored. Mirrors the top-level customization vocabulary.
 */
export function SubmenuEditor({
  parentId,
  items,
  onMoveChildUp,
  onMoveChildDown,
  onToggleChildVisibility,
}: SubmenuEditorProps) {
  const { visible, hidden } = splitChildrenByVisibility(items);
  const handlers = { onMoveChildUp, onMoveChildDown, onToggleChildVisibility };

  return (
    <div className="space-y-3 pt-2">
      <div className="space-y-1.5">
        {visible.map((child, index) => (
          <ChildRow
            key={child.id}
            child={child}
            parentId={parentId}
            hidden={false}
            isFirst={index === 0}
            isLast={index === visible.length - 1}
            {...handlers}
          />
        ))}
      </div>

      {hidden.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            In “More” ({hidden.length})
          </p>
          {hidden.map((child) => (
            <ChildRow
              key={child.id}
              child={child}
              parentId={parentId}
              hidden
              isFirst={false}
              isLast={false}
              {...handlers}
            />
          ))}
        </div>
      )}
    </div>
  );
}
